import React, { useEffect, useState } from 'react';
import { statsApi } from '../services/api';
import { MonthlyStats, YearlyStats, CasinoStats, WeekdayStats, MonthlyCumulativeStats, GroupStats } from '../types';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import './Statistics.css';

const COLORS = ['#ffd700', '#4caf50', '#ff5252', '#2196f3', '#9c27b0', '#ff9800', '#00bcd4', '#e91e63'];

const Statistics: React.FC = () => {
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [monthlyCumulativeStats, setMonthlyCumulativeStats] = useState<MonthlyCumulativeStats[]>([]);
  const [yearlyStats, setYearlyStats] = useState<YearlyStats[]>([]);
  const [casinoStats, setCasinoStats] = useState<CasinoStats[]>([]);
  const [groupStats, setGroupStats] = useState<GroupStats[]>([]);
  const [weekdayStats, setWeekdayStats] = useState<WeekdayStats[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await statsApi.getAvailableYears();
        setAvailableYears(response.data);
        if (response.data.length > 0 && !response.data.includes(selectedYear)) {
          setSelectedYear(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching years:', error);
      }
    };
    fetchYears();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [monthly, monthlyCumulative, yearly, casinos, groups, weekdays] = await Promise.all([
          statsApi.getMonthly(selectedYear),
          statsApi.getMonthlyCumulative(selectedYear),
          statsApi.getYearly(),
          statsApi.getCasinos(),
          statsApi.getGroups(),
          statsApi.getWeekdays()
        ]);

        setMonthlyStats(Array.isArray(monthly.data) ? monthly.data : []);
        setMonthlyCumulativeStats(Array.isArray(monthlyCumulative.data) ? monthlyCumulative.data : []);
        setYearlyStats(Array.isArray(yearly.data) ? yearly.data : []);
        setCasinoStats(Array.isArray(casinos.data) ? casinos.data : []);
        setGroupStats(Array.isArray(groups.data) ? groups.data : []);
        setWeekdayStats(Array.isArray(weekdays.data) ? weekdays.data : []);
      } catch (error) {
        console.error('Error fetching statistics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [selectedYear]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

  const monthlyChartData = monthlyStats.map(stat => ({
    name: monthNames[stat.month - 1],
    Einzahlungen: stat.deposits,
    Auszahlungen: stat.withdrawals,
    Bilanz: stat.balance
  }));

  // Jahresbilanz des gewählten Jahres und Ø Gewinn/Verlust pro aktivem Monat.
  const yearBalance = monthlyStats.reduce((sum, s) => sum + s.balance, 0);
  const activeMonthsCount = monthlyStats.filter(
    s => s.deposits > 0 || s.withdrawals > 0
  ).length;
  const avgPerMonth = activeMonthsCount > 0 ? yearBalance / activeMonthsCount : 0;

  const casinoPieData = casinoStats
    .filter(c => c.deposits > 0 || c.withdrawals > 0)
    .map(c => ({
      name: c.casino_name,
      value: Math.abs(c.balance)
    }));

  const weekdayChartData = weekdayStats.map(stat => ({
    name: stat.weekday_name.substring(0, 2),
    fullName: stat.weekday_name,
    Bilanz: stat.balance,
    Anzahl: stat.transaction_count
  }));

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="statistics-page">
      <h1>Statistiken</h1>

      {/* Monthly Stats */}
      <div className="stat-section">
        <div className="stat-header">
          <h2>Monatliche Übersicht</h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {availableYears.length > 0 ? (
              availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))
            ) : (
              <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
            )}
          </select>
        </div>
        <div className="chart-card">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="name" stroke="#888" />
              <YAxis stroke="#888" tickFormatter={(v) => `${v}€`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend />
              <Bar dataKey="Einzahlungen" fill="#ff5252" />
              <Bar dataKey="Auszahlungen" fill="#4caf50" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="year-summary">
          <div className="year-summary-item">
            <span className="year-summary-label">Jahresbilanz {selectedYear}</span>
            <span className={`year-summary-value ${yearBalance >= 0 ? 'positive' : 'negative'}`}>
              {yearBalance >= 0 ? '+' : ''}{formatCurrency(yearBalance)}
            </span>
          </div>
          <div className="year-summary-item">
            <span className="year-summary-label">Ø Gewinn/Verlust pro Monat</span>
            <span className={`year-summary-value ${avgPerMonth >= 0 ? 'positive' : 'negative'}`}>
              {avgPerMonth >= 0 ? '+' : ''}{formatCurrency(avgPerMonth)}
            </span>
          </div>
        </div>

        {/* Monatsbilanz Liniendiagramm */}
        <h3 className="chart-subtitle">Monatsbilanz {selectedYear}</h3>
        <div className="chart-card">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyCumulativeStats.map(stat => ({
              name: monthNames[stat.month - 1],
              Bilanz: stat.cumulative_balance
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="name" stroke="#888" />
              <YAxis stroke="#888" tickFormatter={(v) => `${v}€`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="Bilanz"
                stroke="#ffd700"
                strokeWidth={3}
                dot={{ fill: '#ffd700', strokeWidth: 2, r: 5 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="monthly-table">
          <table>
            <thead>
              <tr>
                <th>Monat</th>
                <th>Einzahlungen</th>
                <th>Auszahlungen</th>
                <th>Bilanz</th>
              </tr>
            </thead>
            <tbody>
              {monthlyStats.map(stat => (
                <tr key={stat.month}>
                  <td>{monthNames[stat.month - 1]} {stat.year}</td>
                  <td className="negative">{formatCurrency(stat.deposits)}</td>
                  <td className="positive">{formatCurrency(stat.withdrawals)}</td>
                  <td className={stat.balance >= 0 ? 'positive' : 'negative'}>
                    {formatCurrency(stat.balance)}
                  </td>
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>Gesamt</strong></td>
                <td className="negative">
                  <strong>{formatCurrency(monthlyStats.reduce((sum, s) => sum + s.deposits, 0))}</strong>
                </td>
                <td className="positive">
                  <strong>{formatCurrency(monthlyStats.reduce((sum, s) => sum + s.withdrawals, 0))}</strong>
                </td>
                <td className={monthlyStats.reduce((sum, s) => sum + s.balance, 0) >= 0 ? 'positive' : 'negative'}>
                  <strong>{formatCurrency(monthlyStats.reduce((sum, s) => sum + s.balance, 0))}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Yearly Stats */}
      <div className="stat-section">
        <h2>Jahresbilanz</h2>
        <div className="chart-card">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={yearlyStats.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="year" stroke="#888" />
              <YAxis stroke="#888" tickFormatter={(v) => `${v}€`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend />
              <Line type="monotone" dataKey="balance" name="Bilanz" stroke="#ffd700" strokeWidth={3} dot={{ fill: '#ffd700' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="yearly-cards">
          {yearlyStats.map(stat => (
            <div key={stat.year} className="year-card">
              <h3>{stat.year}</h3>
              <div className="year-stats">
                <div>
                  <span className="label">Einzahlungen</span>
                  <span className="value negative">{formatCurrency(stat.deposits)}</span>
                </div>
                <div>
                  <span className="label">Auszahlungen</span>
                  <span className="value positive">{formatCurrency(stat.withdrawals)}</span>
                </div>
                <div>
                  <span className="label">Bilanz</span>
                  <span className={`value ${stat.balance >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(stat.balance)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Casino Stats */}
      <div className="stat-section">
        <h2>Bilanz pro Casino</h2>
        <div className="casino-stats-grid">
          <div className="chart-card">
            {casinoPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={casinoPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }: any) => `${name || ''} (${((percent || 0) * 100).toFixed(0)}%)`}
                  >
                    {casinoPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                    formatter={(value: number) => formatCurrency(value)}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="no-data">Keine Daten vorhanden</p>
            )}
          </div>
          <div className="casino-table">
            <table>
              <thead>
                <tr>
                  <th>Casino</th>
                  <th>Einzahlungen</th>
                  <th>Auszahlungen</th>
                  <th>Bilanz</th>
                  <th>Transaktionen</th>
                </tr>
              </thead>
              <tbody>
                {casinoStats.map((stat, index) => (
                  <tr key={stat.casino_id}>
                    <td>
                      <span className="color-dot" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                      {stat.casino_name}
                    </td>
                    <td className="negative">{formatCurrency(stat.deposits)}</td>
                    <td className="positive">{formatCurrency(stat.withdrawals)}</td>
                    <td className={stat.balance >= 0 ? 'positive' : 'negative'}>
                      {formatCurrency(stat.balance)}
                    </td>
                    <td>{stat.transaction_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top 3 Winners & Losers */}
        <div className="top-lists-grid">
          <div className="top-list-card winners">
            <h3>Top 3 Winner Casinos</h3>
            <div className="top-list">
              {casinoStats
                .filter(c => c.balance > 0)
                .sort((a, b) => b.balance - a.balance)
                .slice(0, 3)
                .map((stat, index) => (
                  <div key={stat.casino_id} className="top-list-item">
                    <span className="rank">{index + 1}</span>
                    <span className="name">{stat.casino_name}</span>
                    <span className="value positive">+{formatCurrency(stat.balance)}</span>
                  </div>
                ))}
              {casinoStats.filter(c => c.balance > 0).length === 0 && (
                <p className="no-data-small">Keine Gewinner</p>
              )}
            </div>
          </div>

          <div className="top-list-card losers">
            <h3>Top 3 Loser Casinos</h3>
            <div className="top-list">
              {casinoStats
                .filter(c => c.balance < 0)
                .sort((a, b) => a.balance - b.balance)
                .slice(0, 3)
                .map((stat, index) => (
                  <div key={stat.casino_id} className="top-list-item">
                    <span className="rank">{index + 1}</span>
                    <span className="name">{stat.casino_name}</span>
                    <span className="value negative">{formatCurrency(stat.balance)}</span>
                  </div>
                ))}
              {casinoStats.filter(c => c.balance < 0).length === 0 && (
                <p className="no-data-small">Keine Verlierer</p>
              )}
            </div>
          </div>

          <div className="top-list-card winners">
            <h3>Top 3 Winner Gruppen</h3>
            <div className="top-list">
              {groupStats
                .filter(g => g.balance > 0)
                .sort((a, b) => b.balance - a.balance)
                .slice(0, 3)
                .map((stat, index) => (
                  <div key={stat.group_id} className="top-list-item">
                    <span className="rank">{index + 1}</span>
                    <span className="name">{stat.group_name}</span>
                    <span className="value positive">+{formatCurrency(stat.balance)}</span>
                  </div>
                ))}
              {groupStats.filter(g => g.balance > 0).length === 0 && (
                <p className="no-data-small">Keine Gewinner</p>
              )}
            </div>
          </div>

          <div className="top-list-card losers">
            <h3>Top 3 Loser Gruppen</h3>
            <div className="top-list">
              {groupStats
                .filter(g => g.balance < 0)
                .sort((a, b) => a.balance - b.balance)
                .slice(0, 3)
                .map((stat, index) => (
                  <div key={stat.group_id} className="top-list-item">
                    <span className="rank">{index + 1}</span>
                    <span className="name">{stat.group_name}</span>
                    <span className="value negative">{formatCurrency(stat.balance)}</span>
                  </div>
                ))}
              {groupStats.filter(g => g.balance < 0).length === 0 && (
                <p className="no-data-small">Keine Verlierer</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Weekday Stats */}
      <div className="stat-section">
        <h2>Bilanz pro Wochentag</h2>
        <div className="chart-card">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weekdayChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="name" stroke="#888" />
              <YAxis stroke="#888" tickFormatter={(v) => `${v}€`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                formatter={(value: number, name: string) => [
                  name === 'Bilanz' ? formatCurrency(value) : value,
                  name
                ]}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
              />
              <Legend />
              <Bar dataKey="Bilanz" fill="#ffd700" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="weekday-cards">
          {weekdayStats.map(stat => (
            <div key={stat.weekday} className={`weekday-card ${stat.balance >= 0 ? 'positive-bg' : 'negative-bg'}`}>
              <h4>{stat.weekday_name}</h4>
              <p className={stat.balance >= 0 ? 'positive' : 'negative'}>
                {formatCurrency(stat.balance)}
              </p>
              <span className="transaction-count">{stat.transaction_count} Transaktionen</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Statistics;
